import { AuthorizedUser, CrudEntity, CrudEntityConstructionOptions, User, UserDetailsWithAttributes } from '@scisys-uim/uim-common';
import { BusinessUnit } from '../../businessunit/model/business-unit.model';
import { ASSETS_USER_DETAILS_METADATA } from './assets-user-details.metadata';
import { AssetsUserDetailsBase } from './assets-user-details.model.base';
import { CostCentre } from '../../costcentre/model/cost-centre.model';


export interface AssetsUserDetailsAttributes extends AssetsUserDetailsBase {
  businessUnit: BusinessUnit;
  costCentres: CostCentre[];
}

export interface AssetsUserDetails extends AssetsUserDetailsAttributes {
}

export interface AssetsUserDetailsTransient { }
export class AssetsUserDetails extends CrudEntity<AssetsUserDetails, string, AssetsUserDetailsTransient> implements AssetsUserDetailsTransient {
  constructor(source?: Partial<AssetsUserDetails>, options?: CrudEntityConstructionOptions) {
    super(ASSETS_USER_DETAILS_METADATA, source, options);
  }
}

export function isUserNationalActor(user: User): boolean {
  return user?.systemProvided || user?.allPrivileges?.find(p => p.name == 'ACTS_AT_NATIONAL_LEVEL') != null;
}
export function isUserBusinessUnitActor(user: User): boolean {
  return user?.systemProvided || user?.allPrivileges?.find(p => p.name == 'ACTS_AT_BUSINESS_UNIT_LEVEL') != null;
}


export interface AssetsUser extends AuthorizedUser, AssetsUserDetailsAttributes {
}

export class AssetsUser extends AuthorizedUser {
  constructor(authorizedUser?: AuthorizedUser) {
    super();
    if (authorizedUser) {
      Object.assign(this, authorizedUser);
    }
  }
  get user(): string {
    return this.username;
  }
  get businessUnit(): BusinessUnit {
    return this.attributes.businessUnit;
  }

  get costCentres(): CostCentre[] {
    return this.attributes.costCentres;
  }

  get isEassetsUser(): boolean {
    return this.componentUserDetailsTypes.indexOf('eAssetsUser') >= 0;
  }

  toAssetsUserDetails(): AssetsUserDetails {
    return new AssetsUserDetails({
      user: this.username,
      businessUnit: this.businessUnit,
      costCentres: this.costCentres
    });
  }

  getDefaultBusinessUnit(): BusinessUnit {
    if (this.businessUnit) {
      return this.businessUnit;
    }
    return null;
  }

  getDefaultCostCentre(): CostCentre {
    if (this.costCentres) {
      for (const costCentre of this.costCentres) {
        if (costCentre && costCentre.businessUnit) {
          return costCentre
        }
      }
    }
    return null;
  }

  isNationalUser(): boolean {
    return this.hasAnyAuthority('ACTS_AT_NATIONAL_LEVEL');
  }

  getEmail() {
    return getUserEmail(this, this.username);
  }

  isSameUser(usernameOrEmail: string): boolean {
    if (this.username == null || usernameOrEmail == null) {
      return false;
    }
    return this.username.toLowerCase() === usernameOrEmail.toLowerCase() ||
      this.getEmail().toLowerCase() === getUserEmail(null, usernameOrEmail).toLowerCase();
  }

  public static assignAs(target: AuthorizedUser): AssetsUser {
    if (target == null) {
      return null;
    }
    if (target instanceof AssetsUser) {
      return target;
    }
    Object.setPrototypeOf(target, AssetsUser.prototype);
    return <AssetsUser>target;
  }
}

export function setDefaultLocationFromUser(user: AuthorizedUser, item: any) {
  if (user) {
    setDefaultBusinessUnitFromUser(user, item);
    setDefaultCostCentreFromUser(user, item);
  }
}

export function setDefaultBusinessUnitFromUser(user: AuthorizedUser, item: any) {
  if (!item || item.businessUnit) {
    return;
  }
  item.businessUnit = getDefaultBusinessUnitFromUser(user);
}


export function getDefaultBusinessUnitFromUser(user: AuthorizedUser): BusinessUnit {
  const assetsUser: AssetsUser = AssetsUser.assignAs(user);
  return assetsUser.getDefaultBusinessUnit();
}

export function getDefaultCostCentreFromUser(user: AuthorizedUser): CostCentre {
  const srpUser: AssetsUser = AssetsUser.assignAs(user);
  return srpUser.getDefaultCostCentre();
}

export function setDefaultCostCentreFromUser(user: AuthorizedUser, item: any) {
  if (!item || item.costCentre) {
    return;
  }
  item.costCentre = getDefaultCostCentreFromUser(user);
}

export function getUserEmail(userDetails: UserDetailsWithAttributes | AssetsUser, username?: string): string {
  var email = null;
  if (userDetails?.attributes?.email != null && userDetails?.attributes?.email.length > 0) {
    email = userDetails?.attributes?.email;
  } else {
    email = userDetails?.username == null ? username : userDetails.username;
  }
  if (email == null) {
    return null;
  }
  if (email.includes('@')) {
    return email;
  }
  return email + "@forestryengland.uk";
}
